import React, { forwardRef, Children } from 'react'
import { useRect } from '@reach/rect'
import { Portal } from 'react-portal'
import { useRef, useCallback, useMemo, useContext } from 'sb-hooks'
import cx from 'classnames'

import MenuItem from './MenuItem'
import MenuLink from './MenuLink'
import MenuContext from './MenuContext'
import type { MenuContextValue, State } from './MenuContext'

import s from './MenuList.scss'


const isFocusableChildType = (child): boolean => [ MenuItem, MenuLink ].includes(child.type)


type MenuListImplProps = {
  ref: (node: any) => void
  refs: MenuContextValue['refs']
  children: any
  className?: string
  state: State
  setState: (state: Partial<State>) => void
  handleListBlur: (event: React.FocusEvent) => void
  handleListKeyDown: (event: React.KeyboardEvent) => void
  handleListMouseEnter: (event: React.MouseEvent) => void
  handleListMouseLeave: (event: React.MouseEvent) => void
}

const MenuListImpl = forwardRef<any, MenuListImplProps>((props, ref) => {
  const {
    refs, children,
    state, setState,
    handleListBlur, handleListKeyDown, handleListMouseEnter, handleListMouseLeave,
    ...rest
  } = props

  const focusableChildren = useMemo<React.ReactNode[]>(() => {
    const focusable = []

    Children.forEach(children, (child) => {
      if (isFocusableChildType(child)) {
        focusable.push(child)
      }
    })

    return focusable
  }, [ children ])

  const setRef = useCallback((node) => {
    refs.current.list = node

    if (ref) {
      if (typeof ref === 'function') {
        ref(node)
      }
      else {
        // @ts-ignore
        ref.current = node
      }
    }
  }, [])

  return (
    <div
      {...rest}
      ref={setRef}
      role="menu"
      tabIndex={-1}
      aria-labelledby={state.buttonId}
      onBlur={handleListBlur}
      onKeyDown={handleListKeyDown}
      onMouseEnter={handleListMouseEnter}
      onMouseLeave={handleListMouseLeave}
    >
      {
        Children.map(children, (child) => {
          if (isFocusableChildType(child)) {
            let focusIndex = focusableChildren.indexOf(child)

            return React.cloneElement(child as React.ReactElement, {
              _ref: (node) => (refs.current.items[focusIndex] = node),
              index: focusIndex,
              state,
              setState,
            })
          }

          return Children.map(children, (child) => (
            React.cloneElement(child as React.ReactElement, {
              _ref: (index) => (node) => (refs.current.items[index] = node),
              state,
              setState,
            })
          ))
        })
      }
    </div>
  )
})


type MenuListRectProps = {
  children?: any
  className?: string
  context: MenuContextValue
}

const MenuListRect = forwardRef<any, MenuListRectProps>(({ children, className, context }, ref) => {
  const {
    refs,
    state, setState,
    getListStyles,
    handleListBlur, handleListKeyDown, handleListMouseEnter, handleListMouseLeave,
  } = context

  const rectRef = useRef()
  const rect = useRect(rectRef) // ATTN useRect should be separated from MenuList bcs instead it returns "undefined" when state.isOpen becomes "true"
  const style = useMemo<{ [key: string]: any }>(() => getListStyles(rect), [ getListStyles, rect ])

  return (
    <Portal>
      <div ref={rectRef} style={style}>
        <MenuListImpl
          ref={ref}
          refs={refs}
          className={cx(className, s.menuList)}
          state={state}
          setState={setState}
          handleListBlur={handleListBlur}
          handleListKeyDown={handleListKeyDown}
          handleListMouseEnter={handleListMouseEnter}
          handleListMouseLeave={handleListMouseLeave}
        >
          {children}
        </MenuListImpl>
      </div>
    </Portal>
  )
})


type MenuListProps = {
  children?: any
  className?: string
}

const MenuList = forwardRef<any, MenuListProps>((props, ref) => {
  const { children, className } = props
  const context = useContext(MenuContext)

  return context.state.isOpen && (
    <MenuListRect className={className} context={context} ref={ref}>
      {children}
    </MenuListRect>
  )
})


export default MenuList
